import { useState, useEffect, useContext } from "react";
import { Button, Flex, Modal, Typography, App, Spin, Pagination } from "antd";

import PlusIcon from "@/icons/PlusIcon";

import UsersTable from "@/components/tables/UsersTable";
import UserModal from "@/components/modals/UserModal";
import Breadcrumbs from "@/components/ui/Breadcrumbs";
import SearchFrom3 from "@/components/inputs/SearchFrom3";
import CustomSelect from "@/components/inputs/CustomSelect";

import { UserContext } from "@/context/UserContext";

import { useTableAutoHeight } from "@/hooks/useTableAutoHeight";

import { getList, deleteItem } from "@/api/users";
import { getUnitsOptions } from "@/api/units";

const LOCAL_STORAGE_KEY = "usersTableParams";

const NOT_FILLED_OPTION = {
  uuid: "notFilled",
  name: "Не заполнено",
  label: "Не заполнено",
};

const { Title } = Typography;

// Страница пользователей
// Содержит список пользователей с возможностью добавления, редактирования и удаления
const Users = () => {
  const { message } = App.useApp();
  const { user } = useContext(UserContext);

  // Данные
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [optionsPending, setOptionsPending] = useState(true);

  const initialTableParams = {
    pagination: {
      current: 1,
      pageSize: 10,
      placement: ["bottomLeft"],
      showSizeChanger: true,
    },
    filters: {
      search: null,
      units_uuids: [],
    },
  };
  const [tableParams, setTableParams] = useState(initialTableParams);

  // Автоматическая высота таблицы
  const { tableContainerRef, tableHeight, headerHeight } = useTableAutoHeight([
    data,
    tableParams.pagination.pageSize,
  ]);

  // Списки для фильтров
  const [units, setUnits] = useState([]);

  // Окно создания/редактирования
  const [isModalShow, setIsModalShow] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);

  // initial fetch
  useEffect(() => {
    fetchOptions();
    // eslint-disable-next-line
  }, []);

  const fetchOptions = async () => {
    setOptionsPending(true);

    try {
      const { units } = await getUnitsOptions();

      setUnits(units);

      // Инициализация параметров из localStorage
      const localParams =
        JSON.parse(
          localStorage.getItem(`${user.user_uuid}_${LOCAL_STORAGE_KEY}`)
        ) || initialTableParams;

      setTableParams(localParams);
      fetchData(localParams);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setOptionsPending(false);
    }
  };

  // Запрос на сервер для получения данных
  const fetchData = async (tParams) => {
    setIsLoading(true);

    const currentParams = tParams || tableParams;
    const params = {
      page: currentParams.pagination.current,
      perpage: currentParams.pagination.pageSize,
      sort: currentParams.sort,
      ...currentParams.filters,
    };

    try {
      const { users, total, page, perPage } = await getList(params);
      setData(users);

      const currentParams = tParams || tableParams;
      const updatedTableParams = {
        ...currentParams,
        pagination: {
          ...currentParams.pagination,
          total,
          current: page,
          pageSize: perPage,
        },
      };
      setParams(updatedTableParams);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Устанавливаем параметры таблицы и сохраняем их в localStorage
  const setParams = (params) => {
    setTableParams(params);

    // Сохраняем параметры пагинации в localStorage
    localStorage.setItem(
      `${user.user_uuid}_${LOCAL_STORAGE_KEY}`,
      JSON.stringify(params)
    );
  };

  const updateFilters = (localParams, newFilters, resetPage = false) => {
    const updatedFilters = { ...localParams.filters, ...newFilters };

    const updatedTableParams = {
      ...localParams,
      filters: updatedFilters,
      pagination: {
        ...localParams.pagination,
        current: resetPage ? 1 : localParams.pagination.current,
      },
    };

    setParams(updatedTableParams);
    fetchData(updatedTableParams);
  };

  // Обработчик для смены фильтров
  const handleFilterChange = async (name, val) => {
    updateFilters(
      tableParams,
      {
        [name]: val || null,
      },
      true
    );
  };

  const handleTableChange = async (pagination, filters, sorter) => {
    const updatedTableParams = {
      ...tableParams,

      pagination: {
        ...tableParams.pagination,
        current: pagination.current || tableParams.pagination.current,
        pageSize: pagination.pageSize || tableParams.pagination.pageSize,
      },
      ...(sorter?.order && {
        sort: `${sorter.order === "descend" ? "-" : ""}${sorter.field}`,
      }),
    };

    setParams(updatedTableParams);
    fetchData(updatedTableParams);
  };

  const handlePaginationChange = (current, pageSize) => {
    // При изменении pageSize сбрасываем страницу на 1
    const shouldResetPage = pageSize !== tableParams.pagination.pageSize;

    const updatedTableParams = {
      ...tableParams,
      pagination: {
        ...tableParams.pagination,
        current: shouldResetPage ? 1 : current,
        pageSize,
      },
    };
    setParams(updatedTableParams);
    fetchData(updatedTableParams);
  };

  // Обработчик добавления нового пользователя
  const handleAddNew = () => {
    setCurrentItem({ idx: data.length });
    setIsModalShow(true);
  };

  // Обработчик редактирования пользователя
  const handleEditItem = (item) => {
    setCurrentItem(item);
    setIsModalShow(true);
  };

  // Обработчик сохранения пользователя
  const handleSaveItem = () => {
    setIsModalShow(false);
    fetchData();
  };

  // Обработчик удаления пользователя
  const handleDeleteItem = (item) => {
    Modal.confirm({
      title: `Вы уверены, что хотите удалить пользователя ${item.email}?`,
      content: "Он больше не сможет осуществлять вход в сервис",
      onOk() {
        handleConfirmDelete(item);
      },
      okText: "Удалить",
      cancelText: "Отмена",
    });
  };

  // Действительно удаляем пользователя после подтверждения
  const handleConfirmDelete = async (item) => {
    setIsLoading(true);
    try {
      await deleteItem({ uuid: item.uuid });
      await fetchData();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
      setIsLoading(false);
    }
  };

  return (
    <>
      {optionsPending ? (
        <div className="spin">
          <Spin size="large" />
        </div>
      ) : (
        <>
          <Flex vertical gap={25} className="h-100">
            {/* Хлебные крошки и заголовок */}
            <Flex vertical gap={10}>
              <Breadcrumbs
                items={[
                  {
                    title: "Административная панель",
                  },
                  {
                    title: "Пользователи",
                  },
                ]}
              />

              <Flex gap={20} align="center" justify="space-between">
                <Title level={2} className="no-margin">
                  Пользователи
                </Title>

                <Button
                  type="primary"
                  icon={<PlusIcon />}
                  onClick={handleAddNew}
                ></Button>
              </Flex>
            </Flex>

            {/* Поиск и фильтры */}
            <Flex gap={8} align="center">
              <SearchFrom3
                placeholder="Поиск"
                style={{ width: "300px" }}
                defaultValue={tableParams.filters.search}
                onSearch={(search) => handleFilterChange("search", search)}
              />
              <CustomSelect
                placeholder="Рабочая группа"
                mode="multiple"
                options={[NOT_FILLED_OPTION, ...units]}
                fieldNames={{
                  label: "name",
                  value: "uuid",
                  key: "uuid",
                }}
                style={{
                  width: 200,
                }}
                optionFilterProp="name"
                allowClear
                maxTagCount="responsive"
                popupMatchSelectWidth={false}
                value={tableParams.filters.units_uuids}
                onChange={(unit) => handleFilterChange("units_uuids", unit)}
              />
            </Flex>

            <div ref={tableContainerRef} style={{ flex: 1, minHeight: 0 }}>
              <UsersTable
                data={data}
                loading={isLoading}
                pagination={false}
                onChange={handleTableChange}
                onEditItem={handleEditItem}
                onDeleteItem={handleDeleteItem}
                scroll={{ x: "100%", y: tableHeight - headerHeight }}
              />
            </div>

            <div style={{ minHeight: 32 }}>
              <Pagination
                {...tableParams.pagination}
                onChange={handlePaginationChange}
              />
            </div>
          </Flex>

          {isModalShow && (
            <UserModal
              data={currentItem}
              visible={isModalShow}
              onSave={handleSaveItem}
              onCancel={() => setIsModalShow(false)}
            />
          )}
        </>
      )}
    </>
  );
};

export default Users;
